/* Las salidas de Buenos Aires que no son bitren, a la SALIDAS de su planilla
 * de furgones.
 *
 * Lautaro Díaz (Satelital), 20/10/2026: «Se me ocurre que en vez de hacer
 * asi podriamos hacer como con torrecillas, que te arrastre el excel y se
 * complete en salidas bitrenes y en salidas y cortes» · «La de only bitrenes
 * es solo para los bitrenes y arribos y cortes solo para furgones normales».
 *
 * ── DE DÓNDE SALE ───────────────────────────────────────────────────────────
 *
 * FlotaBot (`salidas_bitren` del bot) separa las salidas hacia el sur de un
 * día —de la Previsión o del Excel de Gula que él suelta— en bitrenes y
 * `comunes`: los furgones que no son 15xx ni 25xx, en el orden del Excel,
 * con el día de carga y el de salida (en Buenos Aires, el mismo: ticket
 * 445), el furgón (pelado: «733», no «733 (V)»), el tractor (el interno, o
 * la patente como la escribe él: «AF130UR», «JUG 812»), el chofer con nombre
 * completo cuando el padrón lo resuelve sin dudas, y el cliente. Lo «a
 * confirmar» viene con TR y CHOFER vacíos, como en los bitrenes, y lo que
 * todavía no es una salida (sin tractor, sin furgón) viene aparte y no se
 * escribe. Acá sólo se escriben, y él puede destildar cualquiera.
 *
 * ── SU HOJA SALIDAS ─────────────────────────────────────────────────────────
 *
 * En su ARRIBOS Y CORTES: D/C · F/S · FR · TR · CHOFER · CLIENTES LUGAR DE
 * CARGA · y una columna por día («LUNES 19», «MARTES 20»: dónde está cada
 * furgón ese día, que la completa él). Un furgón por renglón, con D/C y F/S
 * como él los escribe: el número del día solo («20»).
 *
 * EL DÍA DE CARGA PUEDE LLAMARSE F/C (ticket 560, Lautaro, 29/10: «CUANDO
 * SUELTO EL EXCEL DE GULA, NO ME APARECE PARA AGREGARLAS A LAS PLANILLAS»).
 * Él rearmó su SALIDAS como F/C · F/S · FURGON · TRACTOR · CHOFER ·
 * CLIENTE/LUGAR DE CARGA, con el título «SALIDAS JUEVES 29/10/2026» arriba:
 * sin «D/C» la hoja no se encontraba y la pantalla decía que no la tenía.
 * F/C (fecha de carga) es lo mismo que D/C, y vale cualquiera de los dos.
 * Como una SALIDAS BITREN también tiene F/C, la hoja que dice BITREN en el
 * nombre o en el encabezado no se toma nunca.
 *
 * Se lee por el TÍTULO de cada columna y no por la posición —sin importar
 * espacios, acentos, barras ni mayúsculas: «D/C», «d / c»—, como
 * `shared/torrecillas`: si mañana suma una columna, sigue andando. Para
 * escribir hacen falta D/C y FR: sin el día y el furgón no se sabe qué ya
 * está, y se repetirían. Una SALIDAS BITREN (con F/C y BITREN, sin D/C) no
 * sirve: los comunes no van nunca a la planilla de bitrenes.
 *
 * ── LO QUE YA ESTÁ NO SE VUELVE A ESCRIBIR ──────────────────────────────────
 *
 * Un furgón ya está si la hoja lo tiene con el mismo D/C. El 20/10 a la
 * mañana él completó a mano las diecinueve salidas comunes del día: soltar
 * el Excel ese día tiene que decir que estaban las diecinueve y no escribir
 * nada. Un D/C combinado (vacío en los renglones de abajo) vale el de arriba.
 *
 * EL FURGÓN SE COMPARA POR SU INTERNO, SIN LO QUE ANOTA TRÁFICO AL LADO
 * (revisión del 20/10). El Excel y la Previsión traen seguido el vacío como
 * «733 (V)», y él escribe FR con el número solo. Comparando con `pelado`,
 * «733V» no era «733»: la vista previa decía «Nuevo» y se escribía otra vez
 * abajo. Ahora la clave saca lo que va entre paréntesis y los «?», y los
 * ceros de adelante de un número (`claveDeFurgon`); los playos con letra
 * («F-135», «F 135») se siguen comparando enteros.
 *
 * ── SUS CELDAS COMBINADAS ───────────────────────────────────────────────────
 *
 * Como en SALIDAS BITREN y ARRIBOS BITREN (#721/#723): escribir debajo de una
 * combinada suya deja el valor donde no se ve. Cada furgón va en el primer
 * renglón vacío, abajo de lo último escrito, donde ninguna combinada suya
 * toca lo que se escribe (D/C, F/S, FR, TR, CHOFER y CLIENTES); los
 * renglones que se saltean quedan como estaban y se devuelven en
 * `salteadas` (números de fila, como se ven) para decirlo en la pantalla.
 *
 * Todo acá es puro: lo prueban los tests con datos inventados.
 */

import { normalizarCombinadas, rangoCombinada } from "./planillaCombinadas";
import { pelado } from "./torrecillas";

const txt = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

/* El título pelado (sin acentos, espacios ni signos) → la columna. */
const COLUMNAS = {
  // «D/C» o «F/C» (día o fecha de carga): ticket 560.
  dc: (p) => p === "DC" || p === "FC",
  fs: (p) => p === "FS",
  fr: (p) => ["FR", "FUR", "FURGON"].includes(p),
  tr: (p) => ["TR", "TRA", "TRAC", "TRACTOR"].includes(p),
  chofer: (p) => ["CHOFER", "CHOFERES"].includes(p),
  // «CLIENTES LUGAR DE CARGA»: el resto del título cambia según quién la armó.
  cliente: (p) => p.startsWith("CLIENTE"),
};

/* `{fila, cols, ultima}` del encabezado (en las primeras seis filas) con D/C
   y FR, o null. `ultima` es la última columna con título. */
export function encabezadoFurgones(hoja) {
  const celdas = hoja?.celdas || [];
  for (let i = 0; i < Math.min(celdas.length, 6); i++) {
    const cols = {};
    let ultima = -1;
    (celdas[i] || []).forEach((v, j) => {
      const p = pelado(v);
      if (p) ultima = j;
      for (const [k, es] of Object.entries(COLUMNAS)) {
        if (cols[k] === undefined && p && es(p)) cols[k] = j;
      }
    });
    // Una SALIDAS BITREN (F/C … BITREN) no es de furgones.
    if ((celdas[i] || []).some((v) => pelado(v) === "BITREN")) continue;
    if (cols.dc !== undefined && cols.fr !== undefined) return { fila: i, cols, ultima };
  }
  return null;
}

/* La hoja SALIDAS de una planilla de furgones (`datos.hojas`), o -1: la que
   se llama SALIDAS (no ARRIBOS ni CORTES, que también tienen D/C y FUR) y
   tiene D/C (o F/C) y FR (o FUR, FURGON); nunca una SALIDAS BITREN. */
export function hojaDeSalidasFurgones(datos) {
  const hojas = datos?.hojas || [];
  return hojas.findIndex((h) => {
    const n = pelado(h.nombre);
    return n.includes("SALIDA") && !n.includes("BITREN") && encabezadoFurgones(h);
  });
}

/* El número del día como lo escribe él: «20», y también «020» o «20/10». */
export function diaDe(v) {
  const m = /^0*(\d{1,2})(?:\D.*)?$/.exec(txt(v));
  return m ? String(Number(m[1])) : "";
}

/* El día de una fecha AAAA-MM-DD: «2026-10-20» → «20». */
const diaDeFecha = (iso) => {
  const m = /^\d{4}-\d{2}-(\d{2})$/.exec(String(iso ?? ""));
  return m ? String(Number(m[1])) : "";
};

/* El furgón para compararlo: «733 (V)» → «733», «0733» → «733», «F-135» →
   «F135»; «???» → "" (no es un furgón). */
export function claveDeFurgon(v) {
  const p = pelado(String(v ?? "").replace(/\([^)]*\)?/g, " ").replace(/\?/g, " "));
  return /^\d+$/.test(p) ? String(Number(p)) : p;
}

const clave = (dia, furgon) => `${dia}|${claveDeFurgon(furgon)}`;

/* Los furgones que la hoja ya tiene: `D/C|FR`. */
function yaEnLaHoja(hoja, enc) {
  const { fila, cols } = enc;
  const hay = new Set();
  let dcArriba = "";
  (hoja.celdas || []).forEach((r, i) => {
    if (i <= fila || !r) return;
    const d = diaDe(r[cols.dc]);
    if (d) dcArriba = d;
    const fr = claveDeFurgon(r[cols.fr]);
    if (fr && (d || dcArriba)) hay.add(clave(d || dcArriba, fr));
  });
  return hay;
}

/* Cuáles de los comunes ya están en la hoja: `Set` de `furgon`. Para la
   vista previa. */
export function cualesYaEstanFurgones(hoja, comunes) {
  const enc = encabezadoFurgones(hoja);
  if (!enc) return new Set();
  const hay = yaEnLaHoja(hoja, enc);
  return new Set(
    (comunes || []).filter((c) => hay.has(clave(diaDeFecha(c.carga), c.furgon))).map((c) => c.furgon),
  );
}

/* Lo que escribe un furgón, en su renglón. */
const HUELLA = ["dc", "fs", "fr", "tr", "chofer", "cliente"];

/* Suma a la hoja los comunes que no tiene, uno por renglón, abajo de lo
   último escrito: lo que él ya escribió no se toca, porque todo renglón que
   se usa está vacío. «Lo último escrito» es el último renglón con algo en
   CUALQUIER columna —un CHOFER, un día—, no el último con FR: si no, un
   renglón suyo con el chofer y sin el furgón quedaría pisado. Devuelve
   `{hoja, agregados, yaEstaban, salteadas}`; la hoja que entra no se toca.
   Nada va debajo de una combinada suya (ver arriba), y las columnas de los
   días quedan para él. Se escriben todos los que entran: los destildados
   los saca la pantalla antes. */
export function agregarASalidasFurgones(hoja, comunes) {
  const enc = encabezadoFurgones(hoja);
  if (!enc) return { hoja, agregados: 0, yaEstaban: 0, salteadas: [] };
  const { fila, cols } = enc;
  const huella = HUELLA.map((k) => cols[k]).filter((c) => c !== undefined);
  const suyas = normalizarCombinadas(hoja.combinadas || []).map(rangoCombinada).filter(Boolean);
  const sirve = (r) => !suyas.some(
    (m) => m.r0 <= r && r <= m.r1 && huella.some((c) => m.c0 <= c && c <= m.c1),
  );
  const celdas = (hoja.celdas || []).map((r) => [...(r || [])]);
  const ancho = Math.max(hoja.cols || 0, ...celdas.map((r) => r.length));
  const hay = yaEnLaHoja(hoja, enc);
  let ultima = fila;
  celdas.forEach((r, i) => {
    if (i > fila && r.some((v) => txt(v))) ultima = i;
  });
  const salteadas = [];
  let agregados = 0;
  let yaEstaban = 0;
  let en = ultima + 1;
  const vacia = () => Array.from({ length: ancho }, () => "");
  for (const c of comunes || []) {
    const dc = diaDeFecha(c.carga);
    const k = clave(dc, c.furgon);
    if (!dc || !claveDeFurgon(c.furgon) || hay.has(k)) {
      if (hay.has(k)) yaEstaban += 1;
      continue;
    }
    while (!sirve(en)) {
      salteadas.push(en + 1);
      en += 1;
    }
    const r = celdas[en] ? [...celdas[en]] : vacia();
    while (r.length < ancho) r.push("");
    const valores = {
      dc, fs: diaDeFecha(c.salida) || dc, fr: txt(c.furgon), tr: txt(c.tractor),
      chofer: txt(c.chofer), cliente: txt(c.cliente),
    };
    // El renglón está vacío entero: va después del último con algo.
    for (const [campo, v] of Object.entries(valores)) {
      if (cols[campo] !== undefined && v) r[cols[campo]] = v;
    }
    celdas[en] = r;
    hay.add(k);
    en += 1;
    agregados += 1;
  }
  for (let i = 0; i < celdas.length; i++) if (!celdas[i]) celdas[i] = vacia();
  return {
    hoja: { ...hoja, celdas, filas: Math.max(hoja.filas || 0, celdas.length), cols: ancho },
    agregados,
    yaEstaban,
    salteadas,
  };
}

/* `{tractor o patente pelada: chofer}` como él lo escribió en las hojas de su
   planilla de furgones (ARRIBOS, CORTES, SALIDAS: la última vez). En CORTES
   el fletero va con su patente en PAT TRA, y ésa también sirve. */
export function choferesDeFurgones(datos) {
  const out = {};
  for (const h of datos?.hojas || []) {
    const celdas = h?.celdas || [];
    for (let i = 0; i < Math.min(celdas.length, 6); i++) {
      const p = (celdas[i] || []).map(pelado);
      const cc = p.findIndex((x) => COLUMNAS.chofer(x));
      const claves = [p.findIndex((x) => COLUMNAS.tr(x)), p.indexOf("PATTRA")].filter((c) => c >= 0);
      if (cc < 0 || !claves.length) continue;
      for (const r of celdas.slice(i + 1)) {
        const c = txt(r?.[cc]).toUpperCase();
        if (!c) continue;
        for (const ct of claves) {
          const t = pelado(r?.[ct]);
          if (t && t !== "FLT") out[t] = c;
        }
      }
      break;
    }
  }
  return out;
}

/* El chofer como lo escribe él, si es el mismo: el apellido (la primera
   palabra) tiene que coincidir. Si no, el que manda FlotaBot, en mayúscula.
   La misma regla que SALIDAS BITREN (`salidasBitren.choferLargo`). */
export function choferComun(chofer, tractor, conocidos = {}) {
  const c = txt(chofer).toUpperCase();
  const suyo = conocidos[pelado(tractor)];
  if (!c || !suyo) return c;
  return pelado(c.split(" ")[0]) === pelado(suyo.split(" ")[0]) ? suyo : c;
}

/* Los comunes que manda FlotaBot → lo que se muestra y se escribe. El
   cliente va como vino: en su SALIDAS lo copia tal cual del Excel
   («RADIO VICTORIA LOG CENTRAL cont x 40»). `id` es el lugar en la lista:
   el mismo furgón dos veces el mismo día son dos renglones (en 105 días del
   Excel no pasó, pero lo que él corrige o destilda de uno no puede caer en
   el otro). */
export function filasDeComunes(comunes, conocidos = {}) {
  return (comunes || []).map((c, i) => ({
    id: String(i),
    furgon: txt(c.furgon),
    tractor: txt(c.tractor),
    chofer: choferComun(c.chofer, c.tractor, conocidos),
    cliente: txt(c.cliente),
    carga: c.carga || "",
    salida: c.salida || c.carga || "",
    confirmado: c.confirmado !== false,
    previsto: c.previsto || null,
  }));
}
